import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PaymentSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", data: null });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ status: "error", data: null });
      return;
    }

    let attempts = 0;
    const MAX_ATTEMPTS = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", data });
          return;
        }
        attempts++;
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", data: null });
      }
    };
    poll();
  }, [location.search]);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-24" data-testid="payment-success-page">
      <div className="bg-white border border-border rounded-3xl p-8 md:p-12 text-center shadow-sm">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-brand-red animate-spin" />
            <h1 className="font-display text-3xl font-bold mt-4">Verifying your payment…</h1>
            <p className="text-muted-foreground mt-2">
              This usually takes just a few seconds.
            </p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600" />
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-4">Payment received!</h1>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
              Thank you — your deposit has been received. Your registration is now marked as{" "}
              <strong>paid</strong>. Confirmation email has been sent.
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button asChild size="lg" className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white">
                <Link to="/dashboard">Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-amber-500" />
            <h1 className="font-display text-3xl font-bold mt-4">
              {state.status === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
              {state.status === "timeout"
                ? "Your payment is still being processed. You can check the status later on your dashboard."
                : "The payment did not go through. You can retry from your dashboard."}
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/dashboard">Back to Dashboard</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-24" data-testid="payment-cancel-page">
      <div className="bg-white border border-border rounded-3xl p-8 md:p-12 text-center shadow-sm">
        <XCircle className="w-16 h-16 mx-auto text-muted-foreground" />
        <h1 className="font-display text-3xl font-bold mt-4">Payment cancelled</h1>
        <p className="text-muted-foreground mt-3">
          You cancelled the checkout. No charge has been made. You can retry the payment anytime from your dashboard.
        </p>
        <Button asChild className="mt-6 rounded-full bg-brand-red hover:bg-brand-red-dark text-white">
          <Link to="/dashboard">Back to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
